import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import axios from 'axios';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(response.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen mystical-bg py-32 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full mx-4"
      >
        <div className="card-mystical rounded-2xl p-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 size={64} className="text-secondary mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-heading text-primary mb-4 uppercase tracking-wider">
                Confirming Your Payment
              </h1>
              <p className="text-primary/70">
                Please wait while we verify your transaction...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={64} className="text-secondary mx-auto mb-6" />
              <h1 className="text-4xl font-heading golden-text text-glow mb-4 uppercase tracking-wider">
                🎉 Welcome to the Shaktiverse!
              </h1>
              <p className="text-xl text-primary/80 mb-8">
                Your enrollment is confirmed. You're now part of the {paymentInfo?.batch} batch.
              </p>
              <div className="card-mystical rounded-xl p-6 mb-8 text-left">
                <h3 className="font-heading text-primary mb-4 uppercase tracking-wider">
                  What's Next?
                </h3>
                <ul className="space-y-3 text-primary/70">
                  <li>✓ Join the Grahita Shakti WhatsApp group: <a href="https://chat.whatsapp.com/IyaMFClKlgY2jUyqtPAdXU" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Click here to join</a></li>
                  <li>✓ You'll receive the class schedule and handouts in the group</li>
                  <li>✓ Prepare for your first session - {paymentInfo?.batch} batch</li>
                  <li>✓ Introduce yourself to fellow seekers in the community</li>
                </ul>
              </div>
              <Button
                onClick={() => navigate('/dashboard')}
                className="ornate-button px-8 py-4 text-lg"
                data-testid="goto-dashboard-button"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-5xl mb-6">⚠️</div>
              <h1 className="text-3xl font-heading text-primary mb-4 uppercase tracking-wider">
                Payment Verification Failed
              </h1>
              <p className="text-primary/70 mb-8">
                We're having trouble verifying your payment. Please check your email for confirmation or contact support.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="ornate-button px-8 py-4"
              >
                Return Home
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;